import { faMinus } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { observe, toJS } from 'mobx';
import { inject, observer } from 'mobx-react';
import * as React from 'react';
import { Prompt, withRouter } from 'react-router-dom';
import { Button, Col, Form, FormGroup, Input, Label, Modal, ModalBody, ModalFooter, ModalHeader, Row, Table, UncontrolledTooltip } from 'reactstrap';
import DataTable from 'src/Components/DataTable';
import { EButton, EInput } from 'src/Components/EInput';
import { MessageStore } from 'src/Stores/Messages/Messages';
import TenantStore from 'src/Stores/Tenant/TenantStore';
import { TestsStore } from 'src/Stores/Tests/TestsStore';
import { IExternalStorage, ITest } from '../../../../../Interfaces/TestInterface';
import { IWell } from '../../../../../Interfaces/WellInterface';
import Footer from '../../../../Footer';
import WorkflowHeader from '../../../WorkflowHeader';
import './VolumeAccounting.scss';

interface IFluidSubTypeTotal{
    subType: string;
    total: number;
}

interface IList {
    headers: any[];
    rows: any[];
}

const editableBlueStyle = {backgroundColor: '#DEEFF5'};
@inject('stores')
@observer
class VolumeAccounting extends React.Component {
    public props: any;
    private dataSet: IList;
    private externalStorage: IExternalStorage = new IExternalStorage();
    private test: ITest = new ITest();
    public TestsStore: TestsStore;
    private testDisposer: any;
    private testListDisposer: any;
    private well: IWell = new IWell();
    public WellsStore: any;
    private wellDisposer: any;
    private TenantStore: TenantStore;
    private MessageStore: MessageStore;

    public state = {
        dataSet: this.dataSet,
        displacementEdited: false,
        editStartingBaseOil: false,
        editing: false,
        externalStorage: this.externalStorage,
        isDirty: false,
        modal: false,
        mudTypes: [],
        oilFluidSubTypeOptions: [],
        removeRowIndex: undefined as unknown as number,
        removeRowModal: false,
        rowIndex: 0,
        sortOrder: 1,
        storageTypeOptions: [],
        test: this.test,
        waterFluidSubTypeOptions: [],
        well: this.well
    };

    constructor(props: any) {
        super(props);

        this.TestsStore = this.props.stores.TestsStore;
        this.WellsStore = this.props.stores.WellsStore;
        this.TenantStore = this.props.stores.TenantStore;
        this.MessageStore = this.props.stores.MessageStore;
    }

    public componentDidMount() {
        this.createDisposers();

        this.getMudTypes();
        this.getStorageTypeOptions();
        this.getFluidSubTypeOptions();
        const viewOnly = this.props.workflowHeaderItems === undefined;
        if (viewOnly) {
            this.WellsStore.getWellReport(this.props.match.params.wellId);
            this.TestsStore.getTestDetailsReport(this.props.match.params.taskId, this.props.match.params.wellId);
        } else if ( this.props.stores.WellsStore.well && this.props.stores.TestsStore.test ) {
            const test = toJS(this.TestsStore.test);
            this.updateCalculations(test);
            this.setState({
                test,
                well: this.WellsStore.well
            }, () => {
                this.formatDataTable();
            });
        } else {
            if ( ! this.TestsStore.test ) {
                this.TestsStore.getTestDetails(this.props.match.params.taskId, this.props.match.params.wellId);
            }
            if ( ! this.WellsStore.well ) {
                this.WellsStore.getWellDetails(this.props.match.params.wellId);
            }
        }
    }

    public createDisposers() {
        this.testDisposer = observe(this.TestsStore, 'test', (change: any) => {
            const test = toJS(change.newValue);
            this.updateCalculations(test);
            this.setState({
                test
            }, () => {
                this.formatDataTable();
            });
        });
        this.wellDisposer = observe(this.WellsStore, 'well', (change: any) => {
            const well = change.newValue;
            this.setState({
                well
            });
        });
        this.testListDisposer = observe(this.TestsStore, 'tests', (change: any) => {
            const test = this.state.test;
            this.updateCalculations(test);
            this.setState({test});
        });
    }

    private renderNumberInput = (row: IExternalStorage, index: number, extStorDisabled: boolean) => (tabIndex: number) => {
        return (
        <EInput
            type="text"
            name="number"
            data-validation="integer"
            value={row.number}
            onChange={this.handleInlineInputChange(index)}
            tabIndex={tabIndex + 51}
            errorCallback={{
                callback: this.isStorageNumberUsed(row.number, index),
                message: 'Each Storage Tank for this well must have a unique number.'
            }}
            disabled={extStorDisabled}
        />
        );
    }

    private renderStorageTypeInput = (row: IExternalStorage, index: number, extStorDisabled: boolean) => (tabIndex: number) => {
        return (
            <EInput
                type="select"
                name="storageType"
                value={row.storageType}
                onChange={this.handleInlineInputChange(index, true)}
                tabIndex={tabIndex + 51}
                required={true}
                disabled={extStorDisabled}
            >
                <option selected={true}/>
                { this.state.storageTypeOptions &&
                    this.state.storageTypeOptions.map((type: any, indexStorage: number) =>
                    <option key={indexStorage} value={type.fieldItemName}>{type.fieldItemName}</option>
                ) }
            </EInput>
        );
    }

    private renderFluidTypeInput = (row: IExternalStorage, index: number, extStorDisabled: boolean) => (tabIndex: number) => {
        return (
            <EInput
                type="select"
                name="fluidType"
                value={row.fluidType}
                onChange={this.handleInlineInputChange(index, true)}
                tabIndex={tabIndex + 51}
                required={true}
                disabled={extStorDisabled}
            >
                <option selected={true}/>
                <option value="OBM">OBM</option>
                <option value="WBM">WBM</option>
            </EInput>     
        );
    }

    private renderFluidSubTypeInput = (row: IExternalStorage, index: number, extStorDisabled: boolean) => (tabIndex: number) => {
        return (
            <EInput
                type="select"
                name="fluidSubType"
                value={row.fluidSubType}
                onChange={this.handleInlineInputChange(index, true)}
                required={true}
                tabIndex={tabIndex + 51}
                disabled={extStorDisabled}
            >
                <option selected={true}/>
                {this.mapFluidTypeOptions(row.fluidType)}
            </EInput>
        );
    }

    private renderTodayFluidAmount = (row: IExternalStorage, index: number, extStorDisabled: boolean) => (tabIndex: number) => {
        return (
            <EInput
                type="text"
                name="todayFluidAmount"
                data-validation="integer"
                required={true}
                value={row.todayFluidAmount}
                onChange={this.handleInlineInputChange(index)}
                tabIndex={tabIndex + 51}
                disabled={extStorDisabled}
            />
        );
    }

    private renderXferIn = (row: IExternalStorage, index: number, extStorDisabled: boolean) => (tabIndex: number) => {
        return (
            <EInput
                type="text"
                name="xferIn"
                value={row.xferIn}
                onChange={this.handleInlineInputChange(index)}
                tabIndex={tabIndex + 51}
                disabled={extStorDisabled}
            />
        );
    }

    private renderXferOut = (row: IExternalStorage, index: number, extStorDisabled: boolean) => (tabIndex: number) => {
        return (
        <EInput
            type="text"
            name="xferOut"
            value={row.xferOut}
            onChange={this.handleInlineInputChange(index)}
            tabIndex={tabIndex + 51}
            disabled={extStorDisabled}
        /> 
        );
    }

    private renderCapacity = (row: IExternalStorage, index: number, extStorDisabled: boolean) => (tabIndex: number) => {
        return (
            <EInput
                type="text"
                name="capacity"
                value={row.capacity}
                onChange={this.handleInlineInputChange(index)}
                tabIndex={tabIndex + 51}
                disabled={extStorDisabled}
            />
        );
    }

    private renderMudWeight = (row: IExternalStorage, index: number, extStorDisabled: boolean) => (tabIndex: number) => {
        return (
            <EInput
                type="text"
                name="mudWeight"
                data-min={0}
                value={row.mudWeight}
                onChange={this.handleInlineInputChange(index, true)}
                tabIndex={tabIndex + 51}
                disabled={extStorDisabled}
            />
        );
    }
    
    private renderOther = (row: IExternalStorage, index: number, extStorDisabled: boolean) => (tabIndex: number) => {
        return (
            <EInput
                type="text"
                name="other"
                value={row.other}
                onChange={this.handleInlineInputChange(index, true)}
                tabIndex={tabIndex + 51}
                disabled={extStorDisabled}
            />
        );
    }

    private renderInUse = (row: IExternalStorage, index: number, extStorDisabled: boolean) => (tabIndex: number) => {
        return (
            <FormGroup check={true} inline={true}>
                <Label check={true} >
                    <Input
                        key={index}
                        type="radio"
                        name={"inUse." + index}
                        id={"inUse" + index}
                        value="true"
                        onChange={this.handleInlineRadioSelect(index)}
                        checked={row.inUse}
                        disabled={extStorDisabled}
                        tabIndex={tabIndex + 51}
                    />
                </Label>
                Yes
                <Label style={{paddingLeft: '5px'}} check={true} >
                    <Input
                        key={index}
                        type="radio"
                        name={"inUse." + index}
                        id={"inUse" + index }
                        value="false"
                        onChange={this.handleInlineRadioSelect(index)}
                        checked={!row.inUse}
                        disabled={extStorDisabled}
                    />
                </Label>
                No
            </FormGroup>
        );
    }


    private formatDataTable() {
        const isAcctTest = this.state.test && this.state.test.isVolumeAccountingReport;
        const isWbmTest = this.state.test && this.state.test.mudProperty === 'Water Based Mud';
        const extStorDisabled = !(isWbmTest || isAcctTest) || this.state.test.testStatus === 'Approved';
        const newDataList: any[] = [];
        const tableHeaders = [
            'Number',
            'Storage Type',
            'Fluid Type',
            'Fluid Sub Type',
            'Prev Fluid Amt',
            'Today Fluid Amt',
            'Transferred In',
            'Transferred Out',
            'Capacity',
            'Mud Weight',
            'Other',
            'In Use'
        ];
        if ( this.state.test.externalStorage ) {
            this.state.test.externalStorage.map((row: IExternalStorage, index: number) => {
                const newObject = [
                    {
                        content: this.renderNumberInput(row, index, extStorDisabled),
                        sortItem: row.number,
                        type: 'render',
                    },
                    {
                        content: this.renderStorageTypeInput(row, index, extStorDisabled),
                        sortItem: row.storageType,
                        type: 'render',
                    },
                    {
                        content: this.renderFluidTypeInput(row, index, extStorDisabled),
                        sortItem: row.fluidType,
                        type: 'render',
                    },
                    {
                        content: this.renderFluidSubTypeInput(row, index, extStorDisabled),
                        sortItem: row.fluidSubType,
                        type: 'render',
                    },
                    {
                        content: row.previousFluidAmount
                    },
                    {
                        content: this.renderTodayFluidAmount(row, index, extStorDisabled),
                        sortItem: row.todayFluidAmount,
                        type: 'render',
                    },
                    {
                        content: this.renderXferIn(row, index, extStorDisabled),
                        sortItem: row.xferIn,
                        type: 'render',
                    },
                    {
                        content: this.renderXferOut(row, index, extStorDisabled),
                        sortItem: row.xferOut,
                        type: 'render',
                    },
                    {
                        content: this.renderCapacity(row, index, extStorDisabled),
                        sortItem: row.capacity,
                        type: 'render',
                    },
                    {
                        content: this.renderMudWeight(row, index, extStorDisabled),
                        sortItem: row.mudWeight,
                        type: 'render',
                    },
                    {
                        content: this.renderOther(row, index, extStorDisabled),
                        sortItem: row.other,
                        type: 'render',
                    },
                    {
                        content: this.renderInUse(row, index, extStorDisabled),
                        sortItem: row.inUse,
                        type: 'render',
                    },
                    {
                        sortItem: row.defaultSortOrdinal,
                        type: 'defaultSort',
                    }
                ];
                newDataList.push(newObject);
            });
        }
        const listStructure = {
            headers: tableHeaders,
            rows: newDataList
        };
        this.setState({
            dataSet: listStructure
        });
    }

    public async componentWillUnmount() {
        this.clearDisposers();
    }

    public clearDisposers() {
        if ( this.testDisposer ) {
            this.testDisposer();
        }
        if ( this.wellDisposer ) {
            this.wellDisposer();
        }
        if ( this.testListDisposer) {
            this.testListDisposer();
        }
    }

    private getMudTypes = () => {
        return this.TestsStore.getMudTypes()
        .then(
            (data: any) => {
                this.setState({
                    mudTypes: data
                });
            }
        );
    }

    private handleReorder = (startIndex: any, endIndex: any) => {
        const test = this.state.test;
        const result = test.externalStorage.sort((a,b) => a.defaultSortOrdinal - b.defaultSortOrdinal);
        const [removed] = result.splice(startIndex, 1);
        result.splice(endIndex, 0, removed);
        result.forEach((val, index) => {
            val.defaultSortOrdinal = index;
        });
        this.setState({
            isDirty: true,
            test
        }, () => {
            this.formatDataTable();
        });
    }

    private changeDefaultSort = (indexes: number[], defaultSortName: string) => {
        const test = this.state.test;
        const result = test.externalStorage;
        result.forEach((val, index) => {
            val[defaultSortName] = indexes[index];
        });

        this.setState({
            isDirty: true,
            test
        }, () => {
            this.formatDataTable();
            this.MessageStore.addMessage("Your custom sort order has been set. Please save the page to save the custom sort order you set.", 'success', 3000);
        });
    }
    // private getMudTypeOptions = () => {
    //     const options: string[] = [];
    //     this.state.mudTypes.map((type: any, index: number) => {
    //         type.subTypeNames.map((sub: string, position: number) => {
    //             options.push(sub);
    //         });
    //     });
    //     return(
    //         options.map((option: string, index: number) =>
    //             <option key={index} value={option}>{option}</option>
    //         )
    //     );
    // }

    private getStorageTypeOptions = () => {
        return this.TestsStore.getStorageTypeOptions()
        .then(
            (data: any) => {
                this.setState({
                    storageTypeOptions: data
                });
            }
        );
    }

    private getFluidSubTypeOptions = () => {
        this.TestsStore.getOilFluidSubTypeOptions()
        .then(
            (data: any) => {
                this.setState({
                    oilFluidSubTypeOptions: data
                });
            }
        );

        this.TestsStore.getWaterFluidSubTypeOptions()
        .then(
            (data: any) => {
                this.setState({
                    waterFluidSubTypeOptions: data
                });
            }
        );
    }

    private mapFluidTypeOptions = (propertySubType: string) => {
        const oilType = propertySubType;
        let options: string[];
        if(oilType === 'OBM') {
            options = this.state.oilFluidSubTypeOptions;
        } else if (oilType === 'WBM') {
            options = this.state.waterFluidSubTypeOptions;
        } else {
            options = [];
        }

        return options.map((option: any, index: number) =>
        <option key={option.id} value={option.fieldItemName}>{option.fieldItemName}</option>);
        
    }
    private toggleModal = () => {
        if ( this.state.modal ) {
            this.setState({
                editing: false,
                externalStorage: new IExternalStorage(),
                modal: !this.state.modal,
            });
        } else {
            this.setState({
                modal: !this.state.modal
            });
        }
    }

    private handleSave = (addAnother: boolean) => () => {
        const test = this.state.test;
        if (!this.state.editing) {
            test.externalStorage.push(this.state.externalStorage);
        } else {
            test.externalStorage[this.state.rowIndex] = this.state.externalStorage;
        }
        this.updateCalculations(test);
        this.setState({
            editing: false,
            externalStorage: new IExternalStorage(),
            isDirty: true,
            test
        }, () => {
            this.formatDataTable();
        });

        if (!addAnother) {
            this.toggleModal();
        }
    }

    private handleDelete = (index: number) => {
        const test = this.state.test;
        const externalStorage = test.externalStorage;
        externalStorage.splice(index, 1);
        test.externalStorage = externalStorage;
        this.setState({
            isDirty: true,
            removeRowIndex: undefined,
            removeRowModal: false,
            test
        }, () => {
            this.formatDataTable();
        });
    }

    private externalStorageTotals = () => {
        let oilTotal: number = 0;
        let waterTotal: number = 0;
        const fluidSubTypeOptionsOil: IFluidSubTypeTotal[] = [];
        const fluidSubTypeOptionsWater: IFluidSubTypeTotal[] = [];

        if (this.state.test.externalStorage.length > 0) {
            this.state.test.externalStorage.map((item: IExternalStorage, index: number) => {
                if (item.inUse) {
                    if (item.fluidType === 'OBM') {
                        oilTotal += Number(item.todayFluidAmount);
                        if (item.fluidSubType !== '') {
    
                            let foundSubType = false;
                            for (const element of fluidSubTypeOptionsOil) {
                                if (item.fluidSubType === element.subType) {
                                    element.total += Number(item.todayFluidAmount);
                                    foundSubType = true;
                                    break;
                                }
                            }
                            
                            if (!foundSubType) {
                                fluidSubTypeOptionsOil.push({
                                    subType: item.fluidSubType,
                                    total: Number(item.todayFluidAmount),
                                });
                            }
                        }
                    } else {
                        waterTotal += Number(item.todayFluidAmount);
                        if (item.fluidSubType !== '') {
    
                            let foundSubType = false;
                            for (const element of fluidSubTypeOptionsWater) {
                                if (item.fluidSubType === element.subType) {
                                    element.total += Number(item.todayFluidAmount);
                                    foundSubType = true;
                                    break;
                                }
                            }
                            
                            if (!foundSubType) {
                                fluidSubTypeOptionsWater.push({
                                    subType: item.fluidSubType,
                                    total: Number(item.todayFluidAmount),
                                });
                            }
                        }
                    }
                }
            });
        }
        return(
            <Row>
                <Col>
                    <div className="data-table-component">
                        <Table style={{textAlign: "center"}}>
                            <thead>
                                <tr>
                                    <th>
                                        Water Total:
                                    </th>
                                    <th>
                                        Oil Total:
                                    </th>
                                </tr>
                            </thead>
                            <tbody>
                                <tr>
                                    <td>
                                        {waterTotal}
                                    </td>
                                    <td>
                                        {oilTotal}
                                    </td>
                                </tr>
                                <tr>
                                    <td style={{padding: "0px"}}>
                                        <table>
                                            <thead>
                                                <tr className="topRowInnerTable">
                                                    { fluidSubTypeOptionsWater.map((element, index) => <th key={index}>{element.subType} Total</th>) }
                                                </tr>
                                            </thead>
                                            <tbody>
                                                <tr className="bottomRowInnerTable">
                                                    { fluidSubTypeOptionsWater.map((element, index) => <td key={index}>{element.total}</td>)}
                                                </tr>
                                            </tbody>
                                        </table>
                                    </td>
                                    <td style={{padding: "0px"}}>
                                        <table>
                                            <thead>
                                                <tr className="topRowInnerTable">
                                                    { fluidSubTypeOptionsOil.map((element, index) => <th key={index}>{element.subType} Total</th>) }
                                                </tr>
                                            </thead>
                                            <tbody>
                                                <tr className="bottomRowInnerTable">
                                                    { fluidSubTypeOptionsOil.map((element, index) => <td key={index}>{element.total}</td>)}
                                                </tr>
                                            </tbody>
                                        </table>
                                    </td>
                                </tr>
                            </tbody>
                        </Table>
                    </div>
                </Col>
            </Row>
        );
    }

    private handleInputChange = (event: any) => {
        const test = this.state.test;
        if (Number.isNaN(Number(event.target.value))) {
            return;
        }
        
        test.volumeAccounting[event.target.name] = Math.round(Number(event.target.value));
        
        this.updateCalculations(test);
        this.setState({
            isDirty: true,
            test
        });
    }

    private handleLossChange = (event: any) => {
        const test = this.state.test;
        if (Number.isNaN(Number(event.target.value))) {
            return;
        }
        
        test.volumeAccounting[event.target.name] = event.target.value;
        
        this.updateCalculations(test);
        this.setState({
            isDirty: true,
            test
        });
    }

    private handleDisplacementChange = (event: any) => {
        const test = this.state.test;
        if (Number.isNaN(Number(event.target.value))) {
            return;
        }
        
        test.volumeAccounting[event.target.name] = Math.round(Number(event.target.value));
        
        this.updateCalculations(test);
        this.setState({
            displacementEdited: true,
            isDirty: true,
            test
        });
    }

    // MUD-2906
    // private handleInputChangeDecimal = (digits?: number) => ( event: any ) => {
    //     if (!digits) {
    //         digits = 0;
    //     }
    //     const test = this.state.test;
    //     let value = test.volumeAccounting[event.target.name];
    //     if(event.key === 'Backspace') {
    //         value = Math.floor(Number(value)) / 10;
    //     } else if (Number.isFinite(Number(event.key))) {
    //         value = Number(value) * 10 + Number(event.key) / 10;
    //     }
        
    //     test.volumeAccounting[event.target.name] = Number(value).toFixed(digits);
    //     this.updateCalculations(test);
    //     this.setState({
    //         isDirty: true,
    //         test
    //     });
    // }

    private handleModalInputChange = (alphaAllowed?: boolean) => (event: any) => {
        const externalStorage = this.state.externalStorage;
        if (alphaAllowed) {
            externalStorage[event.target.name] = event.target.value;
        } else if(Number.isNaN(Number(event.target.value))) {
            return;
        } else {
            externalStorage[event.target.name] = Math.round(Number(event.target.value));
        }
        this.setState({
            externalStorage
        });
    }

    private isStorageNumberUsed = (value: any, indexStorage?: number) => () => {
        const externalStorageList = this.state.test.externalStorage;
        return externalStorageList.some((es, index) => indexStorage !== index && Number(es.number) === Number(value));
    }

    private greyModalSave = () => {
        return this.isStorageNumberUsed(this.state.externalStorage.number)();
    }

    private greyPageSave = () => {
        const externalStorageList = this.state.test.externalStorage;
        return externalStorageList.some((es, index) => this.isStorageNumberUsed(es.number, index)());
    }

    private handleInlineInputChange = ( index: number, alphaAllowed?: boolean ) => ( event:any ) => {
        const externalStorage = this.state.test.externalStorage;
        if (alphaAllowed) {
            externalStorage[index][event.target.name] = event.target.value;
        } else if(Number.isNaN(Number(event.target.value))) {
            return;
        } else {
            externalStorage[index][event.target.name] = Math.round(Number(event.target.value));
        }
        const test = this.state.test;
        test.externalStorage = externalStorage;
        this.updateCalculations(test);
        this.setState({
            isDirty: true,
            test,
        });
    }

    private handleInlineRadioSelect = (index: number) => ( event: any ) => {
        const externalStorage = this.state.test.externalStorage;
        externalStorage[index].inUse = !externalStorage[index].inUse;
        const test = this.state.test;
        test.externalStorage = externalStorage;
        this.updateCalculations(test);
        this.setState({
            isDirty: true,
            test,
        });
    }

    private handleRadioSelect = (event: any) => {
        const externalStorage = this.state.externalStorage;
        const inUse = event.target.value;
        externalStorage[event.target.name] = ( inUse === 'true' );
        this.setState({
            externalStorage
        });
    }

    public handleCheckBox = (event: any) => {
        const test = this.state.test;
        test.volumeAccounting.trackWaterBasedVolume = !test.volumeAccounting.trackWaterBasedVolume;
        this.updateCalculations(test);
        this.setState({
            isDirty: true,
            test
        });
    }

    public handleLocalOverride = (event: any) => {
        switch (event.target.name) {
            case 'startingBaseOilUserOverride':
            {
                this.setState({
                    editStartingBaseOil: !this.state.editStartingBaseOil
                });
            }
        }
        
    }
    public handleUserOverride = (event: any) => {
        const test = this.state.test;
        const va = test.volumeAccounting;
        switch (event.target.name) {
            case 'totalHoleVolumeUserOverride':
            {
                va.totalHoleVolumeUserOverride = !va.totalHoleVolumeUserOverride;
                break;
            }
            case 'externalStorageUserOverride':
            {
                va.externalStorageUserOverride = !va.externalStorageUserOverride;
                break;
            }
            case 'chemicalAdditionsUserOverride':
            {
                va.chemicalAdditionsUserOverride = !va.chemicalAdditionsUserOverride;
                break;
            }
            case 'volOwedToPlantUserOverride':
            {
                va.volOwedToPlantUserOverride = !va.volOwedToPlantUserOverride;
                break;
            }
        }
        this.updateCalculations(test);
        this.setState({
            test,
        });
    }

    private handleCopyFromPrevious = () => {
        const test = this.state.test;
        const copyTank = test.externalStorage[test.externalStorage.length - 1];
        let externalStorage = new IExternalStorage();
        if (copyTank) {
            externalStorage = JSON.parse(JSON.stringify(copyTank));
            externalStorage.id = '';
            externalStorage.number = 0;
            externalStorage.previousFluidAmount = 0;
            externalStorage.todayFluidAmount = 0;
            externalStorage.xferIn = '';
            externalStorage.xferOut = '';
        }

        this.setState({externalStorage});
    }

    private updateCalculations = (test: ITest) => {
         // moved to test store
        this.TestsStore.updateVolumeCalculations(test);

     }

    private updateTestModel = () => {
        let areYouSure = true;
        if (!this.TestsStore.isDisplacementDisabled(this.state.test) && !this.state.displacementEdited)
        {
            areYouSure = confirm('Do you need to adjust the displacement field before saving?\n\nClick OK to Save.');
        }
        if (areYouSure) {
            this.TestsStore.updateTestModel(this.state.test, this.props.match.params.wellId).then((data: any) => {
                if (data) {
                    this.setState({
                        displacementEdited: false,
                        isDirty: false,
                    });
                }
            });
        }
    }
    public sortColumnHeader = (columnName: string) => (event:any) => {
        event.persist();

        const table: any = document.getElementById('sortableTable');
        let sortOrder = this.state.sortOrder;

        const data = this.state.test;

        //
        // This goes through and adds/removes classes based on sorting
        // This will help identify which column is sorted and in what order
        if ( table !== null ) {
            const tbody: any = table.getElementsByTagName('tbody')[0];
            const wasAsc:boolean = event.target.classList.contains('asc');
            const wasPreviouslySorted: boolean = event.target.classList.contains('asc', 'desc');
            const ths: any = table.getElementsByTagName('th');
            const tds: any = tbody.getElementsByTagName('td');
            
            if ( !wasPreviouslySorted ) {
                sortOrder = 1;
                this.setState({
                    sortOrder: 1
                });
            }

            for ( const th of ths ) {
                th.classList.remove('asc', 'desc');
            }

            for ( const td of tds ) {
                td.classList.remove('sorted');
            }

            for ( const row of tbody.rows ) {
                row.cells[event.target.cellIndex].classList.add('sorted');
            }

            if ( wasAsc ) {
                event.target.classList.add('desc');
            } else {
                event.target.classList.add('asc');
            }

        }

        if (data) {
            const sortedData = data.externalStorage.sort((a: IExternalStorage, b:IExternalStorage) => {
                if (typeof a[columnName] === 'string' && isNaN(Number(a[columnName]))) {
                    return a[columnName].localeCompare(b[columnName]) * sortOrder;
                } else if (typeof a[columnName] === 'number' || !isNaN(Number(a[columnName]))) {
                    return (Number(a[columnName]) - Number(b[columnName])) * sortOrder;
                } else if (typeof a[columnName] === 'boolean') {
                    return (a[columnName] === b[columnName] ? 0 : a[columnName] ? -1 : 1) * sortOrder;
                } else {
                    return 0;
                }
            });
            data.externalStorage = sortedData;
            this.setState({
                sortOrder: sortOrder * -1,
                test: data,
            });

        }
    }

    private getCurrentInterval = () => {
        const intervals = this.state.test.intervals;
        if (intervals && intervals.length > 0 && intervals[0]) {
            return intervals[0];
        }
        return undefined;
    }

    private getPropertyTypeCumBaseOil = () => {
        for (const propertyUsed of this.state.test.volumeAccounting.propertyTypeCumBaseOilUseds) {
            if (propertyUsed.propertyType === this.state.test.propertyType) {
                return propertyUsed.cumBaseOilUsed;
            }
        }

        return 0;
    }
    private buttonNavigation = (event: any) => {
        const $target = event.target;
        if ( $target.name === 'previous' ) {if (this.props.workflowHeaderItems[1].some((w: string) => w === 'Dispatches')) {
                this.props.history.push('/well/' + this.props.match.params.wellId + '/test/' + this.props.match.params.taskId + '/inventory/dispatches');
            } else {
                this.props.history.push('/well/' + this.props.match.params.wellId + '/test/' + this.props.match.params.taskId + '/inventory/ebp');
            }

        } else {
            this.props.history.push('/reports/' + this.props.match.params.wellId);
        }
    }

    public showColumns = () => {
        return ( this.state.test.volumeAccounting.trackWaterBasedVolume || this.state.test.mudProperty === 'Oil Based Mud') && this.state.test.isVolumeAccountingReport;
    }

    public render() {
        const copyFromPrevDisabled = !(this.state.test && this.state.test.externalStorage &&  this.state.test.externalStorage.length > 0);
        const currInterval = this.getCurrentInterval();
        const viewOnly = this.props.workflowHeaderItems !== undefined;
        const isAcctTest = this.state.test && this.state.test.isVolumeAccountingReport;
        const isWbmTest = this.state.test && this.state.test.mudProperty === 'Water Based Mud';
        const extStorDisabled = !(isWbmTest || isAcctTest) || this.state.test.testStatus === 'Approved';
        return(
            <div className="VolumeAccounting">
                <Prompt when={this.state.isDirty} message={"Changes have been made.\n\nPress Cancel then Press Save OR Press OK to continue without saving."} />
                {viewOnly && <WorkflowHeader workflowHeaderItems={this.props.workflowHeaderItems} isInventoryReport={this.state.test.isInventoryReport}/>}
                    { this.state.test.mudProperty !== 'Oil Based Mud' && viewOnly && isAcctTest &&
                        <Row style={{'marginBottom': '2rem'}}>
                            <Col md="12">
                                <FormGroup style={{'marginLeft': '1rem'}} check={true}>
                                    <Label check={true}>
                                        <Input
                                            type="checkbox"
                                            name="trackWaterBasedVolume"
                                            id="trackWaterBasedVolume"
                                            checked={this.state.test.volumeAccounting.trackWaterBasedVolume}
                                            onChange={this.handleCheckBox}
                                        />
                                        {' '} Track Volume
                                    </Label>
                                </FormGroup>
                            </Col>
                        </Row>
                    }
                    <Row className="VolumeAccountColumns">
                        { this.showColumns() &&
                            <Col md="2" className="generalVolume">
                            <h5>General Volume (bbls)</h5>
                            <FormGroup>
                                <Label>New Hole Volume</Label>
                                <EInput
                                    type="text"
                                    name="newHoleVolume"
                                    id="newHoleVolume"
                                    value={this.state.test.volumeAccounting.newHoleVolume}
                                    disabled={true}
                                />
                            </FormGroup>
                            <FormGroup>
                                <Label>New Footage Drilled</Label>
                                <EInput
                                    type="text"
                                    name="footageDrilled"
                                    id="footageDrilled"
                                    value={this.state.test.volumeAccounting.footageDrilled}
                                    disabled={true}
                                />
                            </FormGroup>
                            <FormGroup>
                                <Label>Displacement Depth (MD)</Label>
                                <EInput
                                    type="text"
                                    name="displacement"
                                    id="displacement"
                                    value={this.state.test.volumeAccounting.displacement}
                                    onChange={this.handleDisplacementChange}
                                    required={!this.TestsStore.isDisplacementDisabled(this.state.test)}
                                    disabled={!isAcctTest || !this.TestsStore.enableRecentTest() || this.TestsStore.isDisplacementDisabled(this.state.test)}
                                />
                            </FormGroup>
                            <FormGroup>
                                <Label>Other Vol Received</Label>
                                <EInput
                                    type="text"
                                    name="otherReceived"
                                    id="otherReceived"
                                    value={this.state.test.volumeAccounting.otherReceived}
                                    onChange={this.handleInputChange}
                                    disabled={!isAcctTest || !this.TestsStore.enableRecentTest()}
                                />
                            </FormGroup>
                            <div className="EqualsLine">
                            <FormGroup>
                                <Label>Other Vol Transferred</Label>
                                <EInput
                                    type="text"
                                    name="otherTransfer"
                                    id="otherTransfer"
                                    value={this.state.test.volumeAccounting.otherTransfer}
                                    onChange={this.handleInputChange}
                                    disabled={!isAcctTest || !this.TestsStore.enableRecentTest()}
                                />
                            </FormGroup>
                            </div>
                            <FormGroup>
                                <Label>Plant Vol Received</Label>
                                <EInput
                                    type="text"
                                    name="plantVolRecvd"
                                    id="plantVolRecvd"
                                    value={this.state.test.volumeAccounting.plantVolRecvd}
                                    onChange={this.handleInputChange}
                                    disabled={!isAcctTest || !this.TestsStore.enableRecentTest()}
                                />
                            </FormGroup>
                            <FormGroup>
                                <Label>Plant Vol Returned</Label>
                                <EInput
                                    type="text"
                                    name="plantVolReturned"
                                    id="plantVolReturned"
                                    value={this.state.test.volumeAccounting.plantVolReturned}
                                    onChange={this.handleInputChange}
                                    disabled={!isAcctTest || !this.TestsStore.enableRecentTest()}
                                />
                            </FormGroup>
                            <FormGroup>
                                <Label>
                                    {viewOnly && isAcctTest &&
                                    <Input
                                        type="checkbox"
                                        name="volOwedToPlantUserOverride"
                                        id="volOwedToPlantUserOverride"
                                        checked={this.state.test.volumeAccounting.volOwedToPlantUserOverride}
                                        onChange={this.handleUserOverride}
                                    />}
                                    Vol Owed to Plant</Label>
                                <EInput
                                    style={isAcctTest ? editableBlueStyle : {}}
                                    type="text"
                                    name="volOwedToPlant"
                                    id="volOwedToPlant"
                                    value={this.state.test.volumeAccounting.volOwedToPlant}
                                    disabled={!this.state.test.volumeAccounting.volOwedToPlantUserOverride}
                                    onChange={this.handleInputChange}
                                />
                            </FormGroup>
                        </Col>
                        }
                        <Col md={this.showColumns() ? "2" : "4"} className="baseOil">
                            <h5>Base Oil Acctg (gal)</h5>
                            <FormGroup>
                                <Label>
                                    {viewOnly && isAcctTest &&
                                    <Input
                                        type="checkbox"
                                        name="startingBaseOilUserOverride"
                                        id="startingBaseOilUserOverride"
                                        checked={this.state.editStartingBaseOil}
                                        onChange={this.handleLocalOverride}
                                    />}
                                    Starting Base Oil
                                </Label>
                                <EInput
                                    style={isAcctTest ? editableBlueStyle : {}}
                                    type="text"
                                    name="startingBaseOil"
                                    id="startingBaseOil"
                                    value={this.state.test.volumeAccounting.startingBaseOil}
                                    onChange={this.handleInputChange}
                                    disabled={!this.state.editStartingBaseOil || !this.TestsStore.enableRecentTest()}
                                />
                            </FormGroup>
                            <FormGroup>
                                <Label>Base Oil Received</Label>
                                <EInput
                                    type="text"
                                    name="baseOilReceived"
                                    id="baseOilReceived"
                                    value={this.state.test.volumeAccounting.baseOilReceived}
                                    onChange={this.handleInputChange}
                                    disabled={!isAcctTest || !this.TestsStore.enableRecentTest()}
                                />
                            </FormGroup>
                            <FormGroup>
                                <Label>Ending Base Oil</Label>
                                <EInput
                                    type="text"
                                    name="endingBaseOil"
                                    id="endingBaseOil"
                                    value={this.state.test.volumeAccounting.endingBaseOil}
                                    onChange={this.handleInputChange}
                                    disabled={!isAcctTest || !this.TestsStore.enableRecentTest()}
                                />
                            </FormGroup>
                            <FormGroup>
                                <Label>Base Oil Used</Label>
                                <EInput
                                    type="text"
                                    name="baseOilUsed"
                                    id="baseOilUsed"
                                    value={this.state.test.volumeAccounting.baseOilUsed}
                                    disabled={true}
                                />
                            </FormGroup>
                            <FormGroup>
                                <Label>Cum Base Oil Used FLUID: { this.state.test.propertyType }</Label>
                                <EInput
                                    type="text"
                                    name="cumBaseOilUsed"
                                    value={this.getPropertyTypeCumBaseOil() + this.state.test.volumeAccounting.baseOilUsed}
                                    disabled={true}
                                />
                            </FormGroup>
                            <FormGroup>
                                <Label>Cum Base Oil Used INTERVAL: { currInterval && currInterval.intervalType }</Label>
                                <EInput
                                    type="text"
                                    name="cumBaseOilUsed"
                                    value={currInterval && currInterval.cumBaseOilUsed ? currInterval.cumBaseOilUsed
                                        + this.state.test.volumeAccounting.baseOilUsed 
                                        : this.state.test.volumeAccounting.baseOilUsed}
                                    disabled={true}
                                />
                            </FormGroup>
                            <FormGroup>
                                <Label>Cum Base Oil Used TOTAL: Well</Label>
                                <EInput
                                    type="text"
                                    name="cumBaseOilUsed"
                                    value={this.state.test.volumeAccounting.cumBaseOilUsed}
                                    disabled={true}
                                />
                            </FormGroup>
                            <FormGroup id="lostToCuttingsLabel">
                                <Label>Lost to Cuttings Ratio</Label>
                                <Row>
                                    <Col sm={9}>
                                        <EInput
                                            type="text"
                                            name="lostToCuttingsRatio"
                                            id="lostToCuttingsRatio"
                                            value={this.state.test.volumeAccounting.lostToCuttingsRatio}
                                            data-validation="decimal"
                                            decimal-places="1"
                                            onChange={this.handleLossChange}
                                            disabled={!isAcctTest || !this.TestsStore.enableRecentTest()}
                                            autoComplete="off"
                                        />
                                        <UncontrolledTooltip placement="top" target="lostToCuttingsLabel">
                                            Barrels Lost Per Barrel Over Shaker
                                        </UncontrolledTooltip>
                                    </Col>
                                    <Col sm={3}>
                                        <Label><h3>: 1</h3></Label>
                                    </Col>
                                </Row>
                            </FormGroup>
                        </Col>
                        <Col md={this.showColumns() ? "2" : "4"} className="additionsBbbls">
                            <h5>Additions (bbls)</h5>
                            <FormGroup>
                                <Label>Base Oil Used</Label>
                                <EInput
                                    type="text"
                                    name="baseOilUsed"
                                    value={this.state.test.volumeAccounting.baseOil}
                                    disabled={true}
                                />
                            </FormGroup>
                            <FormGroup>
                                <Label>Water</Label>
                                <EInput
                                    type="text"
                                    name="water"
                                    value={this.state.test.volumeAccounting.water}
                                    onChange={this.handleInputChange}
                                    disabled={!isAcctTest || !this.TestsStore.enableRecentTest()}
                                />
                            </FormGroup>
                            <FormGroup>
                                <Label>
                                    {viewOnly && isAcctTest && 
                                    <Input
                                        type="checkbox"
                                        name="chemicalAdditionsUserOverride"
                                        id="chemicalAdditionsUserOverride"
                                        checked={this.state.test.volumeAccounting.chemicalAdditionsUserOverride}
                                        onChange={this.handleUserOverride}
                                    />}
                                    Chemical Additions
                                </Label>
                                <EInput
                                    style={isAcctTest ? editableBlueStyle : {}}
                                    type="text"
                                    name="chemicalAdditions"
                                    id="chemicalAdditions"
                                    value={isAcctTest ? this.state.test.volumeAccounting.chemicalAdditions : ''}
                                    onChange={this.handleInputChange}
                                    disabled={
                                        !isAcctTest 
                                        || !this.state.test.volumeAccounting.chemicalAdditionsUserOverride 
                                        || !this.TestsStore.enableRecentTest()
                                    }
                                />
                            </FormGroup>
                            <FormGroup>
                                <Label>Slurry</Label>
                                <EInput
                                    type="text"
                                    name="slurry"
                                    id="slurry"
                                    value={this.state.test.volumeAccounting.slurry}
                                    onChange={this.handleInputChange}
                                    disabled={!isAcctTest || !this.TestsStore.enableRecentTest()}
                                />
                            </FormGroup>
                            <FormGroup>
                                <Label>Water Flow</Label>
                                <EInput
                                    type="text"
                                    name="washout"
                                    id="washout"
                                    value={this.state.test.volumeAccounting.washout}
                                    onChange={this.handleInputChange}
                                    disabled={!isAcctTest || !this.TestsStore.enableRecentTest()}
                                />
                            </FormGroup>
                            <FormGroup>
                                <Label>Other Additions</Label>
                                <EInput
                                    type="text"
                                    name="otherAdditions"
                                    id="otherAdditions"
                                    value={this.state.test.volumeAccounting.otherAdditions}
                                    onChange={this.handleInputChange}
                                    disabled={!isAcctTest || !this.TestsStore.enableRecentTest()}
                                />
                            </FormGroup>
                            {/* Removed MUD-2997
                            <FormGroup>
                                <Label>New Hole Vol Built</Label>
                                <EInput
                                    type="text"
                                    name="newHoleVolumeAdjustment"
                                    id="newHoleVolumeAdjustment"
                                    value={this.state.test.volumeAccounting.newHoleVolume}
                                    disabled={true}
                                />
                            </FormGroup> */}
                            <FormGroup>
                                <Label>Total Vol Additions</Label>
                                <EInput
                                    type="text"
                                    name="totalAdditions"
                                    id="totalAdditions"
                                    data-validation="decimal"
                                    value={this.state.test.volumeAccounting.totalAdditions}
                                    disabled={true}
                                />
                            </FormGroup>
                        </Col>
                        <Col md={this.showColumns() ? "2" : "4"} className="lossesBbls">
                            <h5>Losses (bbls)</h5>
                            <FormGroup>
                                <Label>Loss to Surface</Label>
                                <EInput
                                    type="text"
                                    name="lossToSurface"
                                    id="lossToSurface"
                                    value={this.state.test.volumeAccounting.lossToSurface}
                                    onChange={this.handleInputChange}
                                    disabled={!isAcctTest || !this.TestsStore.enableRecentTest()}
                                />
                            </FormGroup>
                            <FormGroup>
                                <Label>Loss to Formation</Label>
                                <EInput
                                    type="text"
                                    name="lossToFormation"
                                    id="lossToFormation"
                                    value={this.state.test.volumeAccounting.lossToFormation}
                                    onChange={this.handleInputChange}
                                    disabled={!isAcctTest || !this.TestsStore.enableRecentTest()}
                                />
                            </FormGroup>
                            <FormGroup>
                                <Label>Solids Control</Label>
                                <EInput
                                    type="text"
                                    name="solidsControl"
                                    id="solidsControl"
                                    value={this.state.test.volumeAccounting.solidsControl}
                                    onChange={this.handleInputChange}
                                    disabled={!isAcctTest || !this.TestsStore.enableRecentTest()}
                                />
                            </FormGroup>
                            <FormGroup>
                                <Label>Loss to Cuttings</Label>
                                <EInput
                                    type="text"
                                    name="lossToCuttings"
                                    id="lossToCuttings"
                                    value={this.state.test.volumeAccounting.lossToCuttings}
                                    disabled={true}
                                />
                            </FormGroup>
                            <FormGroup>
                                <Label>Mud Left in Hole</Label>
                                <EInput
                                    type="text"
                                    name="mudLeftInHole"
                                    id="mudLeftInHole"
                                    value={this.state.test.volumeAccounting.mudLeftInHole}
                                    onChange={this.handleInputChange}
                                    disabled={!isAcctTest || !this.TestsStore.enableRecentTest()}
                                />
                            </FormGroup>
                            <FormGroup>
                                <Label>Other Losses</Label>
                                <EInput
                                    type="text"
                                    name="otherLoss"
                                    id="otherLoss"
                                    value={this.state.test.volumeAccounting.otherLoss}
                                    onChange={this.handleInputChange}
                                    disabled={!isAcctTest || !this.TestsStore.enableRecentTest()}
                                />
                            </FormGroup>
                            {/* Removed MUD-2997
                            <FormGroup>
                                <Label>New Hole Vol Deducted</Label>
                                <EInput
                                    type="text"
                                    name="newHoleVolumeAdjustment"
                                    id="newHoleVolumeAdjustment"
                                    value={this.state.test.volumeAccounting.newHoleVolume}
                                    disabled={true}
                                />
                            </FormGroup> */}
                            <FormGroup>
                                <Label>Total Vol Losses</Label>
                                <EInput
                                    type="text"
                                    name="totalLosses"
                                    id="totalLosses"
                                    value={this.state.test.volumeAccounting.totalLosses}
                                    disabled={true}
                                />
                            </FormGroup>
                        </Col>
                    { this.showColumns() &&
                    <Col md="4" className="finalVolInfo">
                        <Row>
                            <Col style={{borderRight: "1px solid #4392f1"}}>
                                <h5>Reconciliation Volume</h5>
                                <Row>
                                    <Col md="2" />
                                    <Col md="10">
                                        <FormGroup>
                                            <Label>                                    
                                                Previous Test Volume
                                            </Label>
                                            <EInput
                                                type="text"
                                                name="prevTotalHoleVolume"
                                                id="prevTotalHoleVolume"
                                                value={this.state.test.volumeAccounting.previousTotalHoleVolume}
                                                onChange={this.handleInputChange}
                                                disabled={true}
                                                />
                                        </FormGroup>
                                    </Col>
                                </Row>
                                <Row>
                                    <Col md="2" className="d-flex align-items-center text-right">
                                        <FontAwesomeIcon icon="plus" color={this.TenantStore.iconColor} />
                                    </Col>
                                    <Col md="10">
                                        <FormGroup>
                                            <Label>Total Vol Additions</Label>
                                            <EInput
                                                type="text"
                                                name="totalAdditions"
                                                id="totalAdditionsRec"
                                                value={this.state.test.volumeAccounting.totalAdditions}
                                                disabled={true}
                                            />
                                        </FormGroup>
                                    </Col>
                                </Row>
                                {/* <Row>
                                    <Col md="2" className="d-flex align-items-center text-right">
                                        <FontAwesomeIcon icon="plus" color={this.TenantStore.iconColor} />
                                    </Col>
                                    <Col md="10"> Removed MUD-4383
                                        <FormGroup>
                                            <Label>New Hole Volume Built</Label>
                                            <EInput
                                                type="text"
                                                name="newHoleVol"
                                                id="newHoleVol"
                                                value={this.state.test.volumeAccounting.newHoleVolume}
                                                disabled={true}
                                            />
                                        </FormGroup>
                                    </Col>
                                </Row> */}
                                <Row> 
                                    <Col md="2" className="d-flex align-items-center text-right">
                                        <FontAwesomeIcon icon={faMinus} color={this.TenantStore.iconColor} />
                                    </Col>
                                    <Col md="10">
                                        <FormGroup>
                                            <Label>
                                                Total Vol Losses
                                            </Label>
                                            <EInput
                                                type="text"
                                                name="externalStorage"
                                                value={this.state.test.volumeAccounting.totalLosses}
                                                disabled={true}
                                            />
                                        </FormGroup>
                                    </Col>
                                </Row>
                                {/* <Row> 
                                    <Col md="2" className="d-flex align-items-center text-right">
                                        <FontAwesomeIcon icon={faMinus} color={this.TenantStore.iconColor} />
                                    </Col>
                                    <Col md="10" className="EqualsLine">
                                        <FormGroup> Removed MUD-4383
                                            <Label>
                                                New Hole Volume Deducted
                                            </Label>
                                            <EInput
                                                type="text"
                                                name="newHoleVolDed"
                                                id="newHoleVolDed"
                                                value={this.state.test.volumeAccounting.newHoleVolume}
                                                disabled={true}
                                            />
                                        </FormGroup>
                                    </Col>
                                </Row> */}
                                <Row className="EqualsLine">
                                    <Col md="2" className="d-flex align-items-center">
                                        <FontAwesomeIcon icon="equals" color={this.TenantStore.iconColor} />
                                    </Col>
                                    <Col md="10">
                                        <FormGroup>
                                            <Label>Reconciliation Factor</Label>
                                            <EInput
                                                type="text"
                                                name="reconciliationFactor"
                                                id="reconciliationFactor"
                                                value={this.state.test.volumeAccounting.previousTotalHoleVolume + this.state.test.volumeAccounting.totalAdditions - this.state.test.volumeAccounting.totalLosses}
                                                disabled={true}
                                                />
                                        </FormGroup>
                                    </Col>
                                </Row>
                            </Col>
                            <Col className="finalVolInfo">
                                <h5>Final Volume</h5>
                                <Row>
                                    <Col md="2" />
                                    <Col md="10">
                                        <FormGroup>
                                            <Label>                                    
                                            {viewOnly && isAcctTest && 
                                            <Input
                                                type="checkbox"
                                                name="totalHoleVolumeUserOverride"
                                                id="totalHoleVolumeUserOverride"
                                                checked={this.state.test.volumeAccounting.totalHoleVolumeUserOverride}
                                                onChange={this.handleUserOverride}
                                                />}
                                                Total Hole Volume
                                            </Label>
                                            <EInput
                                                style={isAcctTest ? editableBlueStyle : {}}
                                                type="text"
                                                name="totalHoleVolume"
                                                id="totalHoleVolume"
                                                value={this.state.test.volumeAccounting.totalHoleVolume}
                                                onChange={this.handleInputChange}
                                                disabled={!isAcctTest || !this.state.test.volumeAccounting.totalHoleVolumeUserOverride || !this.TestsStore.enableRecentTest()}
                                                />
                                        </FormGroup>
                                    </Col>
                                </Row>
                                <Row>
                                    <Col md="2" className="d-flex align-items-center">
                                        <FontAwesomeIcon icon="plus" color={this.TenantStore.iconColor} />
                                    </Col>
                                    <Col md="10">
                                        <FormGroup>
                                            <Label>PVT</Label>
                                            <EInput
                                                type="text"
                                                name="pvt"
                                                id="pvt"
                                                value={this.state.test.pumpData.pitVolume}
                                                disabled={true}
                                                />
                                        </FormGroup>
                                    </Col>
                                </Row>
                                <Row> 
                                    <Col md="2" className="d-flex align-items-center">
                                        <FontAwesomeIcon icon="plus" color={this.TenantStore.iconColor} />
                                    </Col>
                                    <Col md="10" className="EqualsLine">
                                        <FormGroup>
                                            <Label>
                                                External Storage
                                            </Label>
                                            <EInput
                                                type="text"
                                                name="externalStorage"
                                                id="externalStorage"
                                                data-validation="decimal"
                                                value={this.state.test.volumeAccounting.externalStorage}
                                                onChange={this.handleInputChange}
                                                disabled={!isAcctTest || !this.state.test.volumeAccounting.externalStorageUserOverride}
                                            />
                                        </FormGroup>
                                    </Col>
                                </Row>
                                <Row className="EqualsLine">
                                    <Col md="2" className="d-flex align-items-center">
                                        <FontAwesomeIcon icon="equals" color={this.TenantStore.iconColor} />
                                    </Col>
                                    <Col md="10">
                                        <FormGroup>
                                            <Label>Volume on Location</Label>
                                            <EInput
                                                type="text"
                                                name="totalVolume"
                                                id="totalVolume"
                                                value={this.state.test.volumeAccounting.totalVolume}
                                                disabled={true}
                                            />
                                        </FormGroup>
                                    </Col>
                                </Row>
                            </Col> 
                        </Row>
                        <Row>
                            <Col>
                                <FormGroup className="emptyFormGroup" />
                            </Col>
                        </Row>
                        <Row>
                            <Col md={1} />
                            <Col md={5}>
                                <FormGroup>
                                    <EInput
                                        type="text"
                                        name="requiredReconciliation"
                                        id="requiredReconciliation"
                                        value={this.state.test.volumeAccounting.requiredReconciliation}
                                        disabled={true}
                                        />
                                </FormGroup>
                            </Col>
                            <Col md={1} className="d-flex align-items-center">
                                <FontAwesomeIcon icon="equals" color={this.TenantStore.iconColor} />
                            </Col>
                            <Col md={5} className="d-flex align-items-center">
                                <h6>Required Reconciliation</h6>
                            </Col>
                        </Row>
                        <Row>
                            <Col>
                            <p style={{'textAlign': 'center'}}>Total Fluid MUST equal required reconciliation prior to mud report completion and close.</p>
                            </Col>
                        </Row>
                    </Col> }
                    </Row>
                    <Row style={{'marginTop': '2rem'}}>
                        <Col md="12">
                            {this.externalStorageTotals()}
                        </Col>
                        <Col md="12">
                            <DataTable
                                list={this.state.dataSet}
                                onDelete={this.handleDelete}
                                onNew={!extStorDisabled ? {
                                    callback: this.toggleModal,
                                    text: 'Add External Storage'
                                } : undefined}
                                onReorder={!extStorDisabled ? this.handleReorder : undefined}
                                defaultSort={{
                                    change: !extStorDisabled? this.changeDefaultSort : undefined,
                                    index: 12,
                                    name: 'defaultSortOrdinal'
                                }}
                            />
                           
                        </Col>
                    </Row>
                {viewOnly &&                 
                    <Footer 
                        well={this.WellsStore.well}
                        test={this.TestsStore.test}
                        onSave={this.updateTestModel}
                        onNavigate={this.buttonNavigation}
                        saveGrey={this.greyPageSave() || !this.TestsStore.enableRecentTest()}
                        saveDisabled={extStorDisabled && !(isAcctTest)}
                        nextLabel="Reports"
                    />
                }
                <Modal isOpen={this.state.modal} fade={false} toggle={this.toggleModal} backdrop="static">
                    <ModalHeader toggle={this.toggleModal}>
                        { this.state.editing ? 'Edit External Storage' : 'Add External Storage' }
                    </ModalHeader>
                    <ModalBody>
                        <Form>
                            <Row>
                                <Col md="6">
                                    <FormGroup className="required">
                                        <Label>Storage Type</Label>
                                        <EInput
                                            type="select"
                                            name="storageType"
                                            id="storageType"
                                            required={true}
                                            value={this.state.externalStorage.storageType}
                                            onChange={this.handleModalInputChange(true)}
                                        >
                                            <option selected={true}/>
                                            { this.state.storageTypeOptions &&
                                                this.state.storageTypeOptions.map((type: any, index: number) =>
                                                <option key={index} value={type.fieldItemName}>{type.fieldItemName}</option>
                                            ) }
                                        </EInput>
                                    </FormGroup>
                                </Col>
                                <Col md="6">
                                    <FormGroup className="required">
                                        <Label>Fluid Type</Label>
                                        <EInput
                                            type="select"
                                            name="fluidType"
                                            id="fluidType"
                                            required={true}
                                            value={this.state.externalStorage.fluidType}
                                            onChange={this.handleModalInputChange(true)}
                                        >
                                            <option selected={true}/>
                                            <option value="OBM">OBM</option>
                                            <option value="WBM">WBM</option>
                                        </EInput>
                                    </FormGroup>
                                </Col>
                            </Row>
                            <Row>
                                <Col md="6">
                                    <FormGroup className="required">
                                        <Label>Fluid Sub-Type</Label>
                                        <EInput
                                            type="select"
                                            name="fluidSubType"
                                            id="fluidSubType"
                                            required={true}
                                            value={this.state.externalStorage.fluidSubType}
                                            onChange={this.handleModalInputChange(true)}
                                            disabled={!this.TestsStore.enableRecentTest()}
                                        >
                                            <option selected={true}/>
                                            {this.mapFluidTypeOptions(this.state.externalStorage.fluidType)}
                                        </EInput>
                                    </FormGroup>
                                </Col>
                                <Col md="6">
                                    <FormGroup className="required">
                                        <Label>Number</Label>
                                        <EInput
                                            type="text"
                                            name="number"
                                            id="number"
                                            data-validation="integer"
                                            required={true}
                                            value={this.state.externalStorage.number}
                                            onChange={this.handleModalInputChange()}
                                            errorCallback={{
                                                callback: this.isStorageNumberUsed(this.state.externalStorage.number),
                                                message: 'Each Storage Tank for this well must have a unique number.'
                                            }}
                                        />
                                    </FormGroup>
                                </Col>
                                <Col md="6">
                                    <FormGroup className="required">
                                        <Label>Today Fluid Amt</Label>
                                        <EInput
                                            type="text"
                                            name="todayFluidAmount"
                                            id="todayFluidAmount"
                                            data-validation="integer"
                                            required={true}
                                            value={this.state.externalStorage.todayFluidAmount}
                                            onChange={this.handleModalInputChange()}
                                            checked={this.state.externalStorage.inUse}
                                        />
                                    </FormGroup>
                                </Col>
                                <Col md="6">
                                    <FormGroup>
                                        <Label>Capacity</Label>
                                        <EInput
                                            type="text"
                                            data-validation="integer"
                                            name="capacity"
                                            id="capacity"
                                            value={this.state.externalStorage.capacity}
                                            onChange={this.handleModalInputChange()}
                                        />
                                    </FormGroup>
                                </Col>
                                <Col md="6">
                                    <FormGroup>
                                        <Label>Transferred In</Label>
                                        <EInput
                                            type="text"
                                            name="xferIn"
                                            id="xferIn"
                                            value={this.state.externalStorage.xferIn}
                                            onChange={this.handleModalInputChange()}
                                            checked={!this.state.externalStorage.inUse}
                                        />
                                    </FormGroup>
                                </Col>
                                <Col md="6">
                                    <FormGroup>
                                        <Label>Transferred Out</Label>
                                        <EInput
                                            type="text"
                                            name="xferOut"
                                            id="xferOut"
                                            value={this.state.externalStorage.xferOut}
                                            onChange={this.handleModalInputChange()}
                                            checked={!this.state.externalStorage.inUse}
                                        />
                                    </FormGroup>
                                </Col>
                            </Row>
                            <Row>
                                <Col md="6">
                                    <FormGroup>
                                        <Label>Mud Weight</Label>
                                        <EInput
                                            type="text"
                                            name="mudWeight"
                                            id="mudWeight"
                                            value={this.state.externalStorage.mudWeight}
                                            onChange={this.handleModalInputChange(true)}
                                        />
                                    </FormGroup>
                                </Col>
                                <Col md="6">
                                    <FormGroup>
                                        <Label>Description</Label>
                                        <EInput
                                            type="text"
                                            name="other"
                                            id="other"
                                            value={this.state.externalStorage.other}
                                            onChange={this.handleModalInputChange(true)}
                                        />
                                    </FormGroup>
                                </Col>
                            </Row>
                            <Row>
                                <Col md="6">
                                    <Label>In Use</Label><br/>
                                    <FormGroup check={true} inline={true}>
                                        <Label check={true}>
                                        <Input
                                            type="radio"
                                            name="inUse"
                                            id="inUse"
                                            value="true"
                                            onChange={this.handleRadioSelect}
                                            checked={this.state.externalStorage.inUse}
                                        />
                                        </Label> Yes
                                    </FormGroup>
                                    <FormGroup check={true} inline={true}>
                                        <Label check={true}>
                                        <Input
                                            type="radio"
                                            name="inUse"
                                            id="inUse"
                                            value="false"
                                            onChange={this.handleRadioSelect}
                                            checked={!this.state.externalStorage.inUse}
                                        />
                                        </Label> No
                                    </FormGroup>
                                </Col>
                            </Row>
                        </Form>
                    </ModalBody>
                    <ModalFooter>
                        <Button className="secondary" onClick={this.toggleModal}>Cancel</Button>
                        { 
                            !this.state.editing && 
                            <Button color="primary" onClick={this.handleCopyFromPrevious} disabled={copyFromPrevDisabled}>Copy From Previous</Button>
                        }
                        <EButton color="primary" disabled={this.greyModalSave()} onClick={this.handleSave(false)}>Update</EButton>
                        {
                            !this.state.editing &&
                            <EButton color="primary" disabled={this.greyModalSave()} onClick={this.handleSave(true)}>Add Another</EButton>

                        }
                    </ModalFooter>
                </Modal>
            </div>
        );
    }
}

export default withRouter(VolumeAccounting);



// WEBPACK FOOTER //
// ./src/Components/Main/TestWorkflow/Inventory/VolumeAccounting/VolumeAccounting.tsx